import React, { useCallback, useEffect, useRef, useState } from "react";
import siteConfig from "../siteConfig";
import "../styles/accessibility-widget.css";

/**
 * Sitewide accessibility tool (lower-left).
 *
 * Mounted from wrapPageElement in gatsby-browser.js + gatsby-ssr.js rather than
 * from layout.js, because src/pages/menu.js does not render <Layout> and would
 * otherwise be the one page without it.
 *
 * Scope note: these are user display controls. They do not "make the site
 * compliant" — no widget does. Real conformance work (alt text, headings,
 * contrast, focus order) lives in the components themselves; see the README.
 *
 * STORAGE_KEY and the PREF_ATTR names below are duplicated in the pre-paint
 * snippet in src/html.js. Change them in both places or previously saved
 * settings stop being restored.
 */

const STORAGE_KEY = "rhiA11y";

// short key -> data attribute suffix on <html>
const PREF_ATTR = {
  f: "font", // 0-3
  sp: "spacing",
  hc: "contrast",
  rf: "readable",
  hl: "links",
  bc: "cursor",
  pm: "motion",
  rg: "guide",
};

const DEFAULTS = { f: 0, sp: 0, hc: 0, rf: 0, hl: 0, bc: 0, pm: 0, rg: 0 };

const FONT_STEPS = ["100%", "112%", "125%", "150%"];

const readPrefs = () => {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const saved = JSON.parse(raw);
    // Whitelist: never trust storage to hand back the shape we expect.
    return Object.keys(DEFAULTS).reduce(
      (acc, key) => ({ ...acc, [key]: Number(saved[key]) || 0 }),
      {}
    );
  } catch (e) {
    return DEFAULTS;
  }
};

const savePrefs = (prefs) => {
  try {
    const untouched = Object.keys(DEFAULTS).every((k) => !prefs[k]);
    // Leaving no key behind keeps the html.js snippet on its `if (!raw) return`
    // fast path for the ~everyone who never opens this panel.
    if (untouched) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (e) {
    /* private mode / storage disabled — settings just don't persist */
  }
};

const applyPrefs = (prefs) => {
  const root = document.documentElement;
  Object.keys(PREF_ATTR).forEach((key) => {
    const attr = `data-a11y-${PREF_ATTR[key]}`;
    if (prefs[key]) root.setAttribute(attr, String(prefs[key]));
    else root.removeAttribute(attr);
  });
};

/* ------------------------------------------------------------------ icons */

const svg = (children, extra) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...extra}
  >
    {children}
  </svg>
);

const IconAccessibility = () =>
  svg(
    <>
      <circle cx="12" cy="4.5" r="2.3" fill="currentColor" stroke="none" />
      <path d="M4.75 8.6h14.5" />
      <path d="M12 8.6v5" />
      <path d="M12 13.6 8.7 20.4" />
      <path d="M12 13.6l3.3 6.8" />
    </>
  );

const IconClose = () => svg(<path d="M6 6l12 12M18 6L6 18" />);
const IconContrast = () =>
  svg(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3v18a9 9 0 000-18z" fill="currentColor" stroke="none" />
    </>
  );
const IconFont = () =>
  svg(
    <>
      <path d="M4 19l5.5-14L15 19" />
      <path d="M6.2 14h7.1" />
      <path d="M17.5 19V9.5M17.5 19h3" />
    </>
  );
const IconSpacing = () =>
  svg(
    <>
      <path d="M4 5h16M4 12h16M4 19h16" />
      <path d="M12 8.2 12 2.6M9.6 5 12 2.6 14.4 5" />
      <path d="M12 15.8 12 21.4M9.6 19 12 21.4 14.4 19" />
    </>
  );
const IconLink = () =>
  svg(
    <>
      <path d="M10.5 13.5a4 4 0 006 .5l2-2a4 4 0 00-5.7-5.7l-1.1 1.1" />
      <path d="M13.5 10.5a4 4 0 00-6-.5l-2 2a4 4 0 005.7 5.7l1.1-1.1" />
    </>
  );
const IconCursor = () =>
  svg(<path d="M5 3l13 10h-6l3.2 6.6-2.9 1.4L9.1 14.4 5 18z" />);
const IconPause = () => svg(<path d="M9 5v14M15 5v14" />);
const IconGuide = () =>
  svg(
    <>
      <path d="M4 6h16M4 18h16" opacity="0.55" />
      <rect x="2.5" y="9.5" width="19" height="5" rx="1.2" />
    </>
  );

/* Order matters — this is the tab order and the reading order. */
const TILES = [
  { key: "hc", label: "High contrast", Icon: IconContrast },
  { key: "rf", label: "Readable font", Icon: IconFont },
  { key: "sp", label: "Text spacing", Icon: IconSpacing },
  { key: "hl", label: "Highlight links", Icon: IconLink },
  { key: "bc", label: "Big cursor", Icon: IconCursor },
  { key: "pm", label: "Pause animations", Icon: IconPause },
  { key: "rg", label: "Reading guide", Icon: IconGuide, wide: true },
];

const FOCUSABLE = "button:not(:disabled), a[href]";

/* ------------------------------------------------------------- component */

const AccessibilityWidget = () => {
  // Lazy initialiser, not an effect: on the client's first render this reads
  // back exactly what the html.js snippet already put on <html>, so the effect
  // below is a no-op re-write instead of a visible reset-then-restore flash.
  // Safe for hydration because nothing rendered here depends on it.
  const [prefs, setPrefs] = useState(readPrefs);
  const [open, setOpen] = useState(false);

  const fabRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    applyPrefs(prefs);
    savePrefs(prefs);
  }, [prefs]);

  const set = useCallback(
    (key, value) => setPrefs((prev) => ({ ...prev, [key]: value })),
    []
  );

  const close = useCallback(() => {
    setOpen(false);
    if (fabRef.current) fabRef.current.focus();
  }, []);

  /* Esc anywhere, and a pointer press outside the panel that is not the FAB
     (the FAB's own onClick already toggles — letting this fire too would close
     and immediately reopen). */
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      }
    };
    const onPointerDown = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (fabRef.current && fabRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [open, close]);

  // Move focus into the panel on open so a keyboard/screen-reader user lands on
  // the dialog rather than continuing past it into the page.
  useEffect(() => {
    if (open && panelRef.current) panelRef.current.focus();
  }, [open]);

  const onPanelKeyDown = (e) => {
    if (e.key !== "Tab" || !panelRef.current) return;
    const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && (e.target === first || e.target === panelRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && e.target === last) {
      e.preventDefault();
      first.focus();
    }
  };

  /* Reading guide: a real DOM node on <body>, created only while the toggle is
     on, so the pointermove listener costs nothing for everyone else. */
  useEffect(() => {
    if (!prefs.rg) return undefined;
    const bar = document.createElement("div");
    bar.className = "rhi-a11y-guide";
    bar.setAttribute("aria-hidden", "true");
    bar.style.transform = "translateY(-100px)";
    document.body.appendChild(bar);

    let frame = 0;
    let y = 0;
    const onMove = (e) => {
      y = e.clientY;
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        bar.style.transform = `translateY(${y - 28}px)`;
      });
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      if (frame) window.cancelAnimationFrame(frame);
      bar.remove();
    };
  }, [prefs.rg]);

  const phoneHref = `tel:${siteConfig.phoneE164(siteConfig.phone)}`;

  return (
    <div className="rhi-a11y">
      <button
        type="button"
        ref={fabRef}
        className="rhi-a11y__fab"
        aria-expanded={open}
        /* Only while it exists: aria-controls pointing at an absent id is what
           axe flags as "needs review", and this site's Lighthouse a11y score is
           at 100 — not worth spending on an attribute aria-expanded already
           covers for the collapsed state. */
        aria-controls={open ? "rhi-a11y-panel" : undefined}
        aria-label="Accessibility settings"
        onClick={() => setOpen((v) => !v)}
      >
        <IconAccessibility />
      </button>

      {open && (
        <div
          id="rhi-a11y-panel"
          ref={panelRef}
          className="rhi-a11y__panel"
          role="dialog"
          aria-modal="true"
          aria-label="Accessibility settings"
          tabIndex={-1}
          onKeyDown={onPanelKeyDown}
        >
          <div className="rhi-a11y__header">
            <IconAccessibility />
            <h2 className="rhi-a11y__title">Accessibility</h2>
            <button
              type="button"
              className="rhi-a11y__close"
              onClick={close}
              aria-label="Close accessibility settings"
            >
              <IconClose />
            </button>
          </div>

          <div className="rhi-a11y__body">
            <span className="rhi-a11y__legend" id="rhi-a11y-textsize">
              Text size
            </span>
            <div
              className="rhi-a11y__stepper"
              role="group"
              aria-labelledby="rhi-a11y-textsize"
            >
              <button
                type="button"
                className="rhi-a11y__step"
                onClick={() => set("f", Math.max(0, prefs.f - 1))}
                disabled={prefs.f === 0}
                aria-label="Decrease text size"
              >
                A&minus;
              </button>
              <span className="rhi-a11y__value" aria-live="polite">
                {FONT_STEPS[prefs.f]}
                <span className="rhi-a11y__sr"> text size</span>
              </span>
              <button
                type="button"
                className="rhi-a11y__step"
                onClick={() =>
                  set("f", Math.min(FONT_STEPS.length - 1, prefs.f + 1))
                }
                disabled={prefs.f === FONT_STEPS.length - 1}
                aria-label="Increase text size"
              >
                A+
              </button>
            </div>

            <span className="rhi-a11y__legend">Display options</span>
            <div className="rhi-a11y__grid">
              {TILES.map(({ key, label, Icon, wide }) => (
                <button
                  key={key}
                  type="button"
                  className={`rhi-a11y__tile${
                    wide ? " rhi-a11y__tile--wide" : ""
                  }`}
                  aria-pressed={prefs[key] === 1}
                  onClick={() => set(key, prefs[key] ? 0 : 1)}
                >
                  <Icon />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="rhi-a11y__footer">
            <button
              type="button"
              className="rhi-a11y__reset"
              onClick={() => setPrefs(DEFAULTS)}
            >
              Reset all
            </button>
            <p className="rhi-a11y__help">
              Trouble using this site? Call{" "}
              <a href={phoneHref}>{siteConfig.phone}</a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessibilityWidget;
